import { forwardRef, useRef } from "react";
import type { ComponentPropsWithoutRef, CSSProperties, JSX } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";

import { DashboardWidgetDefinition } from "src/types/dashboard";
import DashboardWidgetContent from "src/components/builder/DashboardWidgetContent";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";
import { openTableMenu } from "src/charts/tableMenuRegistry";

type WidgetRendererProps = Omit<ComponentPropsWithoutRef<"div">, "children"> & {
  widget: DashboardWidgetDefinition;
  dashboard_name: string;
  appliedFilters: Record<string, unknown>;
};

type WidgetCssVars = CSSProperties & Record<`--${string}`, string>;

const WidgetTooltip = forwardRef<HTMLDivElement, { description: string }>(
  (props, ref): JSX.Element => {
    return (
      <span
        ref={ref}
        className="d-inline-flex align-items-center text-muted"
        role="button"
        tabIndex={0}
        aria-label={props.description ?? "Widget description"}
        {...props}
      >
        <i className="bi bi-question-circle" />
      </span>
    );
  },
);

const WidgetRenderer = forwardRef<HTMLDivElement, WidgetRendererProps>(
  (
    { widget, className, style, dashboard_name, appliedFilters, ...rest },
    ref,
  ): JSX.Element => {
    const { x, y, w, h } = widget.grid;
    const { min_w, min_h, max_w, max_h } = widget.grid_constraints;
    const headerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    const { data, isLoading, isError } = useQuery({
      queryKey: ["widget", dashboard_name, widget.id, appliedFilters],
      queryFn: async () =>
        getDashboardWidgetDataApi(dashboard_name, String(widget.id), {
          filters: appliedFilters,
        }),
      enabled:
        !!dashboard_name &&
        Object.keys(appliedFilters).length > 0 &&
        widget.chart_type !== "special_quadrant_chart" &&
        widget.chart_type !== "tiered_bar" &&
        // these fetch internally so their Top-N selectors can refetch
        widget.chart_type !== "custom_lines" &&
        widget.chart_type !== "custom_table" &&
        widget.chart_type !== "deviation_bar" &&
        widget.chart_type !== "average_bar" &&
        widget.chart_type !== "cards",
      staleTime: 0,
      gcTime: 0,
    });

    const rawResponse = data as
      | { data?: unknown; metadata?: Record<string, unknown> }
      | undefined;
    const widgetData =
      widget.chart_type === "quadrant_table"
        ? {
            practices: Array.isArray(rawResponse?.data) ? rawResponse.data : [],
            tier_rules: rawResponse?.metadata?.tier_rules ?? {},
          }
        : widget.chart_type === "donut_pie" ||
            widget.chart_type === "info_bar" ||
            widget.chart_type === "bar_drilldown" ||
            widget.chart_type === "bar_drilldown_custom"
          ? // keep the envelope — these widgets read metadata too
            // (donut_pie: totals; info_bar: insufficient-data breakout;
            // bar_drilldown/bar_drilldown_custom: headline/status/technical
            // detail rows)
            rawResponse
          : (rawResponse?.data ?? rawResponse);

    const widgetStyle: WidgetCssVars = {
      width: "100%",
      height: "100%",
      border: "var(--bs-border-width) solid var(--bs-border-color)",
      borderRadius: "var(--bs-card-border-radius)",
      backgroundColor: "var(--bs-card-bg)",
      color: "var(--bs-body-color)",
      "--widget-x": String(x),
      "--widget-y": String(y),
      "--widget-w": String(w),
      "--widget-h": String(h),
      "--widget-min-w": String(min_w),
      "--widget-min-h": String(min_h),
      "--widget-max-w": String(max_w),
      "--widget-max-h": String(max_h),
    };

    const mergedStyle: WidgetCssVars = {
      ...widgetStyle,
      ...(style as CSSProperties),
    };

    const mergedClassName = className
      ? `card shadow-sm ${className}`
      : "card shadow-sm";

    const openExportingMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
      const containerId = `${widget.id}`;
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = { x: rect.right, y: rect.bottom };

      // openTableMenu is a no-op (returns false) when not in table mode.
      // In table mode it calls ChartTableView's registered setter directly — no DOM guessing.
      if (openTableMenu(containerId, pos)) return;

      // Normal chart mode: fire the Highcharts hamburger menu.
      const container = document.getElementById(containerId);
      const exportBtn = container?.querySelector(
        ".highcharts-contextbutton",
      ) as HTMLElement;
      ((exportBtn as any)?.hcEvents?.click?.[0] as any)?.fn();
    };

    // Chart types with no context menu (pure display widgets, no chart/conversion).
    // kpi and sparkle now register a download menu (KpiExportMenu) under the
    // widget id, so the header hamburger works for them via openTableMenu.
    const NO_MENU_TYPES = new Set([
      "kpi_with_detail",
      "data_table",
      "special_quadrant_chart",
      "custom_bar",
      "custom_table",
      "info_bar",
      "donut_pie",
      "custom_lines",
      "deviation_bar",
      "average_bar",
      "cards",
      "tiered_bar",
      // no embedded Highcharts instance to proxy the shell's hamburger into
      "bar_drilldown_custom",
    ]);

    return (
      <div ref={ref} className={mergedClassName} style={mergedStyle} {...rest}>
        <div
          ref={headerRef}
          className="card-header d-flex align-items-center justify-content-between gap-2"
        >
          <h4 className="mb-0 text-truncate">{widget.title}</h4>
          {/* flex + shrink-0 keeps the buttons on one line however narrow the
              widget gets (a floated block let the icons wrap underneath) */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {!NO_MENU_TYPES.has(widget.chart_type) ? (
              <button
                onClick={openExportingMenu}
                className="btn p-0"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="5"
                    width="18"
                    height="3"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="3"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="3"
                    y="17"
                    width="18"
                    height="3"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </button>
            ) : null}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip
                  id={`widget-description-${String(widget.id ?? widget.widget_template_id)}`}
                >
                  {widget.description ?? "No description available"}
                </Tooltip>
              }
            >
              <WidgetTooltip
                description={widget.description ?? "No description available"}
              />
            </OverlayTrigger>
          </div>
        </div>

        <div
          ref={bodyRef}
          className={`card-body d-flex flex-fill flex-column overflow-auto ${
            ["data_table"].includes(widget.chart_type)
              ? "px-0 pt-2 justify-content-center"
              : ["special_quadrant_chart", "tiered_bar"].includes(
                    widget.chart_type,
                  )
                ? "p-0"
                : ["sparkle"].includes(widget.chart_type)
                  ? ""
                  : "justify-content-center"
          }`}
        >
          <DashboardWidgetContent
            chartType={widget.chart_type}
            title={widget.title}
            description={widget.description}
            data={widgetData}
            isLoading={isLoading}
            hasError={isError && !isLoading ? true : false}
            chartSpec={widget.chart_spec}
            dashboardName={dashboard_name}
            widgetTemplateId={String(widget.id)}
            appliedFilters={appliedFilters}
          />
        </div>
      </div>
    );
  },
);

WidgetRenderer.displayName = "WidgetRenderer";

export default WidgetRenderer;
